import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Paper, Alert } from "@/ui";

import AddCompanyForm, { Company } from "@/components/AddCompanyForm";
import type {
  DigitalLender,
  CustomerDeclarationEntry,
  ApiResponse,
  ServiceResponse,
  CalculatedTotals,
} from "@/shared/api/types";
import type { LenderParentRecord } from "@/shared/api/services/lenderService";
import { LenderService } from "@/shared/api/services/lenderService";
import { httpGet } from "@/shared/api/http";
import {
  customerEndpoints,
  declarationEndpoints,
} from "@/shared/api/endpoints";
import { femaCapture } from "@/shared/fema/capture";
import DashboardLayout from "@/components/DashboardLayout";
import { getCustomerId } from "@/shared/lib/utils/getCustomerId";
import { SharePageService } from "@/shared/api/services/sharePageService";

const AddCompanyPage: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [summary, setSummary] = useState<CalculatedTotals>({
    customerId: 0,
    totalIncome: 0,
    totalAssets: 0,
    totalOutgoingsFixed: 0,
    totalOutgoingsFlexible: 0,
    totalOwingOnPriorityDebt: 0,
    totalRepayOnPriorityDebt: 0,
    totalOwingOnNonPriorityDebt: 0,
    totalRepayOnNonPriorityDebt: 0,
    pendingPaymentOffer: 0,
    balanceAmount: 0,
    totalRepaymentDebts: 0,
    totalOwingOnDebts: 0,
  });

  const [, setCompanyShareLastUpdateDate] = useState<number>(0);
  const [ieMstrLenderData, setIeMstrLenderData] = useState<
    LenderParentRecord[]
  >([]);
  const [digitalLenders, setDigitalLenders] = useState<DigitalLender[]>([]);
  const [freepostCreditorNamesData, setFreepostCreditorNamesData] = useState<
    DigitalLender[]
  >([]);
  const [declarationData, setDeclarationData] = useState<
    CustomerDeclarationEntry[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const hasFetchedSummary = useRef(false);

  // GET summary
  useEffect(() => {
    if (hasFetchedSummary.current) return;
    hasFetchedSummary.current = true;

    async function getSummary(): Promise<ServiceResponse<any[]>> {
      try {
        const resolvedCustomerId: string | number | null =
          await getCustomerId();

        const response = await httpGet<ApiResponse>(
          customerEndpoints.getCalculatedTotals(Number(resolvedCustomerId))
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_calculated_totals_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getSummary().then((res: any) => {
      setSummary(res[0]);
    });
  }, []);

  useEffect(() => {
    const fetchCompanies = async (): Promise<Company[]> => {
      const resolvedCustomerId: string | number | null = await getCustomerId();

      if (!resolvedCustomerId) {
        return [];
      }

      try {
        const coerceNumericId = (value: unknown): number | null => {
          if (typeof value === "number" && Number.isFinite(value)) return value;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed) return null;
            const parsed = Number(trimmed);
            return Number.isFinite(parsed) ? parsed : null;
          }
          return null;
        };

        const isSharedRow = (row: any): boolean => {
          const submit =
            typeof row?.submitDate === "string" ? row.submitDate.trim() : "";
          const lastShared =
            typeof row?.lastSharedDate === "string"
              ? row.lastSharedDate.trim()
              : "";
          return Boolean(submit || lastShared);
        };

        /**
         * IMPORTANT (legacy parity):
         * - Keep ALL rows that were already shared (submitDate/lastSharedDate present),
         *   because history must remain visible (and reference edits after sharing create a new row).
         * - Only dedupe *pending/unshared* duplicates (no submitDate/lastSharedDate), since these
         *   are the "company shows twice" bug scenario.
         */
        const dedupeSharingRows = (rows: any[]): any[] => {
          const bestPendingByKey = new Map<string, any>();

          const keyForRow = (row: any): string => {
            const debtId = coerceNumericId(row?.debtId);
            const id = coerceNumericId(row?.id);
            if (typeof debtId === "number") return `debt:${debtId}`;
            if (typeof id === "number") return `id:${id}`;
            return `fallback:${String(row?.lenderName ?? row?.companyName ?? "")}|${String(
              row?.accountNumber ?? row?.referenceNumber ?? ""
            )}`;
          };

          // Pass 1: choose the "best" pending row per key (latest lastUpdateDate)
          for (const row of rows) {
            if (isSharedRow(row)) continue;
            const key = keyForRow(row);
            const prev = bestPendingByKey.get(key);
            const prevTs = prev?.lastUpdateDate
              ? Date.parse(prev.lastUpdateDate)
              : NaN;
            const nextTs = row?.lastUpdateDate
              ? Date.parse(row.lastUpdateDate)
              : NaN;
            if (
              !prev ||
              (Number.isFinite(nextTs) &&
                (!Number.isFinite(prevTs) || nextTs >= prevTs))
            ) {
              bestPendingByKey.set(key, row);
            }
          }

          // Pass 2: rebuild list preserving original order:
          // - include all shared rows
          // - include only the chosen pending row per key
          const emittedPendingKeys = new Set<string>();
          const out: any[] = [];
          for (const row of rows) {
            if (isSharedRow(row)) {
              out.push(row);
              continue;
            }
            const key = keyForRow(row);
            if (emittedPendingKeys.has(key)) continue;
            if (bestPendingByKey.get(key) === row) {
              out.push(row);
              emittedPendingKeys.add(key);
            }
          }
          return out;
        };

        const digitalDataRaw: any =
          await SharePageService.getCustomerSharing(resolvedCustomerId);
        const digitalData: any[] = Array.isArray(digitalDataRaw)
          ? dedupeSharingRows(digitalDataRaw)
          : [];

        const customerData = JSON.parse(
          localStorage.getItem("customerData") as string
        );

        const freePostDataRaw: any = await SharePageService.getFreePostData(
          customerData.customerId
        );
        const freePostData: any[] = Array.isArray(freePostDataRaw)
          ? dedupeSharingRows(freePostDataRaw)
          : [];

        const data = [
          ...(await Promise.all(
            digitalData.map(async (company: any) => {
              if (!company.lenderName) {
                return {};
              }

              // const correspondingDebt = debts.find((entry: any) => {
              //   return entry.id == company.debtId;
              // });

              let type = "all";

              const obj: Company = {
                id: company.id,
                name: company.lenderName,
                reference: company.accountNumber,
                type: type as Company["type"],
                address: company.address || "",
                documents: company.documents || [],
                selectedDocuments: [],
                lastShared: company.lastShared || "",
                raw: company,
              };

              return obj;
            })
          )),

          ...freePostData.map((company: any) => {
            let type = "all";

            const shareDate = company.lastSharedDate || company.submitDate;

            let obj: Company = {
              id: company.id,
              name: company.companyName,
              reference: company.referenceNumber,
              type: type as Company["type"],
              address: company.address || "",
              documents: company.documents || [],
              selectedDocuments: [],
              lastShared: company.lastShared || "",
              raw: company,
              isFp: true,
            };

            return obj;
          }),
        ];

        return data;
      } catch (error) {
        console.error("Failed to fetch companies", error);
        return [];
      }
    };

    fetchCompanies().then((companiesArray) => {
      setCompanies(companiesArray);
    });
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchLookups = async () => {
      try {
        setLoading(true);
        setError(null);

        const customerId = await getCustomerId();
        if (!customerId) {
          setError("Missing customer context. Please sign in again.");
          return;
        }

        const [lendersResp, digitalResp, freepostResp, declarationResp] =
          await Promise.all([
            LenderService.fetchLenders({ skipErrorCapture: true }),
            httpGet<ApiResponse<DigitalLender[]>>(
              customerEndpoints.listDigitalLenders
            ),
            httpGet<ApiResponse<DigitalLender[]>>(
              customerEndpoints.listFreepostLenders
            ),
            httpGet<ServiceResponse<CustomerDeclarationEntry[]>>(
              declarationEndpoints.getDeclarationAll(Number(customerId))
            ),
          ]);

        if (!mounted) {
          return;
        }

        const lendersData = Array.isArray(lendersResp?.data)
          ? lendersResp.data
          : [];
        const digitalData = Array.isArray(digitalResp?.data)
          ? (digitalResp.data ?? [])
          : [];
        const freepostData = Array.isArray(freepostResp?.data)
          ? (freepostResp.data ?? [])
          : [];
        const declarationRaw =
          (declarationResp as any)?.data &&
          Array.isArray((declarationResp as any).data)
            ? (declarationResp as any).data
            : Array.isArray(declarationResp)
              ? declarationResp
              : [];

        setIeMstrLenderData(lendersData);
        setDigitalLenders(digitalData.filter(Boolean));
        setFreepostCreditorNamesData(freepostData.filter(Boolean));
        setDeclarationData(declarationRaw.filter(Boolean));
      } catch (err) {
        setError("Failed to load company lists. Please try again later.");
        femaCapture("add_company_page_lookups_failed", err, {
          category: "api",
          severity: "warning",
          skipSanitization: false,
          user: {},
        });
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void fetchLookups();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <DashboardLayout backgroundColor="#f8f9fa">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "24px", md: "30px" },
              lineHeight: "36px",
              color: "#343a40",
              mb: 0.5,
            }}
          >
            Add Company
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: "16px", lineHeight: "24px", color: "#6c757d" }}
          >
            Select a company to share your budget with. If you can't find your
            company, we can also send your budget by Freepost.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Paper
          sx={{
            p: 3,
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            border: "1px solid #e9ecef",
          }}
        >
          <AddCompanyForm
            ieMstrLenderData={ieMstrLenderData}
            digitalLenders={digitalLenders}
            setCompanies={setCompanies}
            freepostCreditorNamesData={freepostCreditorNamesData}
            declarationData={declarationData}
            setCompanyShareLastUpdateDate={setCompanyShareLastUpdateDate}
            onBack={() => navigate(-1)}
            onSuccess={() => navigate(-1)}
            companies={companies}
            balanceAmount={summary.balanceAmount}
          />
        </Paper>
      </Box>
    </DashboardLayout>
  );
};

export default AddCompanyPage;
